import { useRef, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useNavigate } from "react-router-dom";
import Checkbox from "app/components/common/inputs/Checkbox";
import UploadIcon from "app/assets/icons/UploadIcon";
import Loader from "app/components/common/Loader";
import ConnectBankBox from "app/components/imagesUploads/joint/ConnectBank";
import UploadNewBankStatement from "app/components/imagesUploads/joint/UploadNewBankStatement";
import httpRequest from "app/lib/axios/index";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { handleApiError } from "app/utils/handleApiError";

interface FormData {
  statement: File[] | null;
  bankStatementTerms: boolean;
}

const PartSeven: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const { bankStatement, bankStatementTerms, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  );
  const { applicantID, ...savedIds } = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const frontFileInputRef = useRef<HTMLInputElement>(null);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<FormData>({
    defaultValues: {
      bankStatementTerms: false,
    },
  });
  const statement = watch("statement");
  const terms = watch("bankStatementTerms");

  const onSubmit = async (data: FormData) => {
    if (!applicantID) {
      toast.error("Applicant id is required");
      return;
    }

    const { statement, bankStatementTerms } = data;

    if (
      !Array.isArray(statement) ||
      statement.length === 0 ||
      !statement.every((file) => file instanceof File)
    ) {
      dispatch(
        addStepsData({
          ...others,
          bankStatementTerms,
        })
      );
      navigate(`/signatures`);
      return;
    }

    try {
      setIsLoading(true);
      const documentTypes = statement.map((_, i) => "bank_statement");

      getPresignedUrls(
        {
          url: `/document/${applicantID}/presigned-urls`,
          data: { documentTypes },
        },
        {
          onSuccess: async (presignedData) => {
            const uploads: Promise<void>[] = [];
            const metadata: Record<
              string,
              { key: string; fileName: string; mimeType: string }
            > = {};

            statement.forEach((file: File, index: number) => {
              const type =
                index === 0 ? "bank_statement" : `bank_statement_${index}`;
              const presigned = presignedData[type];

              if (presigned?.url && presigned?.key) {
                // Upload file to S3
                const uploadPromise = fetch(presigned.url, {
                  method: "PUT",
                  headers: {
                    "Content-Type": file.type,
                  },
                  body: file,
                }).then(() => {});

                uploads.push(uploadPromise);
                metadata[type] = {
                  key: presigned.key,
                  fileName: file.name,
                  mimeType: file.type,
                };
              }
            });

            await Promise.all(uploads);

            if (Object.keys(metadata).length === 0) {
              toast.error("Metadata is empty, upload may have failed.");
              return;
            }
            const bankStatements = Object.values(metadata);

            await httpRequest.post(`/document/${applicantID}/bank-statement`, {
              bankStatements,
            });

            const urls = Object.values(metadata).map((m) => m.key);
            dispatch(
              addStepsData({
                ...others,
                bankStatement: urls,
                bankStatementTerms,
              })
            );
            navigate("/signatures");
            setIsLoading(false);
          },
        }
      );
    } catch (error: any) {
      handleApiError(error);
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    clearErrors("statement");

    if (!files || files.length === 0) {
      e.target.value = "";
      setValue("statement", null);
      return;
    }

    const newFilesArray = Array.from(files);
    const existingFiles = (watch("statement") || []) as File[];

    const filteredNewFiles = newFilesArray.filter(
      (newFile) =>
        !existingFiles.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
        )
    );

    const totalFiles = existingFiles.length + filteredNewFiles.length;

    if (totalFiles > 10) {
      setError("statement", {
        type: "maxFiles",
        message: "You can upload up to 10 files only",
      });
      e.target.value = "";
      // setValue("statement", null);
      return;
    }

    const invalidFiles = filteredNewFiles.filter(
      (file) =>
        file.size > 5 * 1024 * 1024 ||
        !["image/jpeg", "image/png", "application/pdf"].includes(file.type)
    );

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map((file) => {
        if (file.size > 5 * 1024 * 1024) return `${file.name} exceeds 5MB`;
        return `${file.name} has an invalid type`;
      });

      setError("statement", {
        type: "validation",
        message: errorMessages.join("\n"),
      });

      e.target.value = "";
      setValue("statement", null);
      return;
    }

    const updatedFiles = [...existingFiles, ...filteredNewFiles];
    setValue("statement", updatedFiles);
    clearErrors("statement");

    if (frontFileInputRef.current) frontFileInputRef.current.value = "";
  };

  const handleDeleteImage = (indexToDelete: number) => {
    const currentFiles = watch("statement") as File[] | null;
    if (!statement && bankStatement && bankStatement?.length >= 0) {
      const updatedFiles = bankStatement.filter(
        (_, index) => index !== indexToDelete
      );
      dispatch(
        addStepsData({
          ...others,
          bankStatement: updatedFiles || [],
        })
      );
    } else {
      if (!currentFiles) return;

      const updatedFiles = currentFiles.filter(
        (_, index) => index !== indexToDelete
      );

      setValue("statement", updatedFiles.length > 0 ? updatedFiles : null);
    }
  };

  const handleConnectBank = () => {
    if (savedIds?.bankDocLink) {
      window.open(savedIds?.bankDocLink, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (bankStatementTerms !== null && bankStatementTerms !== undefined) {
      setValue("bankStatementTerms", bankStatementTerms);
    }
  }, [setValue, bankStatementTerms]);

  return (
    <>
      {isLoading && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <ProgressBar progress={100} MB={7} />
      <div className="w-full sm:w-[80%] xl:w-[55%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Securely Verify Your Finances " />
        <SubTitle title="To help us assess your loan eligibility, please choose one of the following secure options to share your financial information" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left">
            <div className="flex flex-col xl:flex-row  md:flex-col gap-4 sm:gap-5">
              <ConnectBankBox
                onClick={handleConnectBank}
                className="w-full  sm:w-full xl:w-[49%]  "
              />
              <div className="w-full sm:w-full xl:w-[49%] relative ">
                <UploadNewBankStatement
                  label="Upload bank statements"
                  description="Accepted formats: JPG, PNG, PDF"
                  name="statement"
                  register={register}
                  onChange={handleFileChange}
                  inputRef={frontFileInputRef}
                  disabled={false}
                  icon={<UploadIcon />}
                  required={!(terms || (bankStatement?.length || 0) > 0)}
                  // uploadedFiles={statement || bankStatement}
                  uploadedFiles={
                    (statement && statement.length > 0
                      ? statement
                      : bankStatement) || []
                  }
                  handleDeleteImage={handleDeleteImage}
                  hasSavedFiles={!!statement?.length || !!bankStatement?.length}
                />

                {errors.statement && (
                  <ErrorsMessage
                    title={errors.statement.message}
                    className="absolute top-[100%] left-0"
                  />
                )}
              </div>
            </div>
            <div className="mt-7 sm:mt-6">
              <h6 className="font-Mulish text-base font-semibold text-[#313131]">
                Why We Ask for This
              </h6>
              <p className="text-[#585C7B] font-Mulish text-base font-normal">
                We use your financial information to responsibly assess your
                ability to meet loan repayments. Verifying your income and
                expenses helps us make faster, fairer lending decisions — all
                while keeping your data secure and confidential.
              </p>
            </div>

            <div className="mt-7">
              <Controller
                name="bankStatementTerms"
                control={control}
                // rules={{ required: "You must agree to the Privacy Policy." }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <div className="mb-3  sm:mb-1 text-left">
                      <Checkbox
                        id="terms"
                        labelClassName="sm:mt-[-15px]"
                        labelHtml={
                          <>
                            <span className="text-[#313131] text-[12px]">
                              I have connected bank account
                            </span>
                          </>
                        }
                        checked={value}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          onChange(isChecked);
                          if (isChecked) {
                            clearErrors("statement");
                          }
                        }}
                      />
                    </div>
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isLoading}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartSeven;
